// src/pages/dashboard/DashboardRektor.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, CheckCircle, AlertTriangle, TrendingUp,
  Clock, BarChart3, ArrowUpRight, Calendar, Sparkles, Shield,
  ChevronRight, Hourglass, Send, Gavel, CalendarDays, CalendarRange
} from 'lucide-react';
import { Card, Badge, Breadcrumb, Loading } from '../../components/ui';
import { DashboardData, TingkatRisiko } from '../../types';
import {
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend,
  BarChart, Bar,
} from 'recharts';
import api from '../../services/api';
import { formatDate } from '../../utils/formatDate';

// ========== CONSTANTS ==========
const RISK_COLORS: Record<string, string> = {
  rendah: '#22C55E',
  sedang: '#F59E0B',
  tinggi: '#F97316',
  kritis: '#EF4444',
};

const KATEGORI_COLORS = ['#66C9CC', '#47AEC9', '#3D93BD', '#3A6BA6', '#6366F1', '#14B8A6'];

const CHART_COLORS: Record<string, string> = {
  draf: '#94A3B8',
  diajukan: '#3B82F6',
  validasi_l1: '#06B6D4',
  validasi_l2: '#A855F7',
  antrean_rapim: '#F97316',
  diputuskan: '#22C55E',
  berlangsung: '#EAB308',
  menunggu_konfirmasi: '#F59E0B',
  selesai: '#10B981',
  ditolak_l1: '#EF4444',
  ditolak_l2: '#DC2626',
  diarsipkan: '#9CA3AF',
};

const STATUS_LABELS_SHORT: Record<string, string> = {
  draf: 'Draft',
  diajukan: 'Diajukan',
  validasi_l1: 'Val. Dekan',
  validasi_l2: 'Val. Warek',
  ditolak_l1: 'Tolak L1',
  ditolak_l2: 'Tolak L2',
  antrean_rapim: 'RAPIM',
  diputuskan: 'Keputusan',
  berlangsung: 'Proses',
  menunggu_konfirmasi: 'Konfirmasi',
  selesai: 'Selesai',
  diarsipkan: 'Arsip',
};

// Status Flow Configuration
const STATUS_FLOW = [
  { key: 'draf', label: 'Draft', icon: FileText, color: '#94A3B8', bgColor: 'bg-slate-100' },
  { key: 'diajukan', label: 'Diajukan', icon: Send, color: '#3B82F6', bgColor: 'bg-blue-100' },
  { key: 'validasi_l1', label: 'Val. Dekan', icon: CheckCircle, color: '#06B6D4', bgColor: 'bg-cyan-100' },
  { key: 'validasi_l2', label: 'Val. Warek', icon: Shield, color: '#A855F7', bgColor: 'bg-purple-100' },
  { key: 'antrean_rapim', label: 'RAPIM', icon: Gavel, color: '#F97316', bgColor: 'bg-orange-100' },
  { key: 'berlangsung', label: 'Proses', icon: Clock, color: '#EAB308', bgColor: 'bg-yellow-100' },
  { key: 'menunggu_konfirmasi', label: 'Konfirmasi', icon: Hourglass, color: '#F59E0B', bgColor: 'bg-amber-100' },
  { key: 'selesai', label: 'Selesai', icon: CheckCircle, color: '#10B981', bgColor: 'bg-emerald-100' },
];

// ✅ NEW: Trend Granularity Options
type TrendGranularity = 'harian' | 'mingguan' | 'bulanan';

const TREND_OPTIONS: { id: TrendGranularity; label: string; icon: React.ReactNode }[] = [
  { id: 'harian', label: 'Harian', icon: <CalendarDays className="w-3.5 h-3.5" /> },
  { id: 'mingguan', label: 'Mingguan', icon: <CalendarRange className="w-3.5 h-3.5" /> },
  { id: 'bulanan', label: 'Bulanan', icon: <Calendar className="w-3.5 h-3.5" /> },
];

const defaultData: DashboardData = {
  total_laporan: 0,
  laporan_per_status: {
    draf: 0, diajukan: 0, validasi_l1: 0, ditolak_l1: 0,
    validasi_l2: 0, ditolak_l2: 0, antrean_rapim: 0,
    diputuskan: 0, tugas_ppepp_aktif: 0, berlangsung: 0,
    menunggu_konfirmasi: 0, selesai: 0, diarsipkan: 0,
  },
  laporan_overdue: 0,
  rata_rata_penyelesaian: 0,
  laporan_per_risiko: { rendah: 0, sedang: 0, tinggi: 0, kritis: 0 },
  progress_ppepp: 0,
  tren_bulanan: [],
  laporan_per_kategori: [],
  rapim_terdekat: [],
  laporan_terbaru: [],
  overdue_list: [],
};

// ========== HELPER FUNCTIONS ==========
const toRiskLevel = (risk: string | undefined): TingkatRisiko => {
  const validRisks: TingkatRisiko[] = ['rendah', 'sedang', 'tinggi', 'kritis'];
  const normalized = (risk || 'sedang').toLowerCase() as TingkatRisiko;
  return validRisks.includes(normalized) ? normalized : 'sedang';
};

interface PendingItem {
  id: number;
  kode: string;
  judul: string;
  status: string;
  tingkat_risiko?: string;
  risiko?: string;
  unit?: { id: number; name: string };
  user?: { id: number; name: string };
  created_at?: string;
}

// ✅ NEW: Interface untuk Trend Data
interface TrendDataItem {
  label: string;
  laporan_masuk: number;
  laporan_selesai: number;
  // Fallback fields dari tren_bulanan
  bulan?: string;
  jumlah?: number;
  selesai?: number;
}

// ========== COMPONENT ==========
const DashboardRektor: React.FC = () => {
  const [data, setData] = useState<DashboardData>(defaultData);
  const [antreanRapim, setAntreanRapim] = useState<PendingItem[]>([]);
  const [rapimTerdekatFallback, setRapimTerdekatFallback] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // ✅ NEW: Trend States
  const [trendGranularity, setTrendGranularity] = useState<TrendGranularity>('bulanan');
  const [trendData, setTrendData] = useState<TrendDataItem[]>([]);
  const [trendLoading, setTrendLoading] = useState(false);

  const normalizeOverdueDays = (value: unknown): number => {
    const parsed = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(0, Math.round(Math.abs(parsed)));
  };

  // ✅ Main Dashboard Fetch
  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);

        const [dashboardRes, rapimRes, rapatRes] = await Promise.all([
          api.get('/dashboard'),
          api.get('/rapim/available-laporan', { params: { per_page: 5 } }).catch(() =>
            api.get('/laporan', { params: { status: 'antrean_rapim', per_page: 5 } }).catch(() => ({
              data: { data: [] }
            }))
          ),
          api.get('/rapim', { params: { per_page: 20 } }).catch(() => ({
            data: { data: [] }
          })),
        ]);

        if (dashboardRes.data?.data) {
          setData({ ...defaultData, ...dashboardRes.data.data });
          // Set initial trend data from dashboard (bulanan)
          const bulananData = dashboardRes.data.data.tren_bulanan || [];
          setTrendData(normalizeTrendData(bulananData, 'bulanan'));
        } else if (dashboardRes.data) {
          setData({ ...defaultData, ...dashboardRes.data });
          const bulananData = dashboardRes.data.tren_bulanan || [];
          setTrendData(normalizeTrendData(bulananData, 'bulanan'));
        }

        const rapimData = rapimRes.data?.data || rapimRes.data || [];
        setAntreanRapim(Array.isArray(rapimData) ? rapimData : []);

        const rapatPayload = rapatRes.data?.data || rapatRes.data || [];
        const rapatItems = Array.isArray(rapatPayload)
          ? rapatPayload
          : (Array.isArray((rapatPayload as any)?.data) ? (rapatPayload as any).data : []);
        const normalizedRapat = rapatItems
          .map((item: any) => ({
            id: item.id,
            judul: item.judul,
            tanggal: item.tanggal,
            tempat: item.tempat,
            status: item.status,
            jumlah_peserta: item.jumlah_peserta ?? item.peserta_count ?? 0,
            jumlah_laporan: item.jumlah_laporan ?? item.laporan_masalah_count ?? 0,
          }))
          .filter((item: any) =>
            !!item.id &&
            !!item.tanggal &&
            ['dijadwalkan', 'berlangsung'].includes(String(item.status || '').toLowerCase())
          )
          .sort((a: any, b: any) => new Date(a.tanggal).getTime() - new Date(b.tanggal).getTime());
        setRapimTerdekatFallback(normalizedRapat);
      } catch (err) {
        console.error('Failed to load dashboard:', err);
        setData(defaultData);
        setRapimTerdekatFallback([]);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  // ✅ NEW: Normalize Trend Data untuk konsistensi
  const normalizeTrendData = (rawData: any[], granularity: TrendGranularity): TrendDataItem[] => {
    if (!Array.isArray(rawData)) return [];

    return rawData.map((item) => ({
      label: item.label || item.bulan || item.tanggal || item.minggu || '-',
      laporan_masuk: item.laporan_masuk ?? item.jumlah ?? 0,
      laporan_selesai: item.laporan_selesai ?? item.selesai ?? 0,
    }));
  };

  // ✅ NEW: Fetch Trend Data when granularity changes
  useEffect(() => {
    const fetchTrendData = async () => {
      // Jika bulanan, gunakan data dari dashboard (sudah di-load)
      if (trendGranularity === 'bulanan' && data.tren_bulanan && data.tren_bulanan.length > 0) {
        setTrendData(normalizeTrendData(data.tren_bulanan, 'bulanan'));
        return;
      }

      setTrendLoading(true);
      try {
        const res = await api.get('/monitoring/trend', {
          params: { granularity: trendGranularity }
        });

        const rawTrend = res.data?.data || res.data || [];
        setTrendData(normalizeTrendData(rawTrend, trendGranularity));
      } catch (err) {
        console.error('Failed to fetch trend data:', err);
        // Fallback ke data bulanan dari dashboard
        if (data.tren_bulanan) {
          setTrendData(normalizeTrendData(data.tren_bulanan, 'bulanan'));
        }
      } finally {
        setTrendLoading(false);
      }
    };

    if (!loading) {
      fetchTrendData();
    }
  }, [trendGranularity, loading, data.tren_bulanan]);

  // Generate AI-like smart insights text
  const generateInsights = () => {
    const overdueCount = data.laporan_overdue || 0;
    const selesaiCount =
      (data.laporan_per_status?.selesai || 0) +
      ((data.laporan_per_status as any)?.diarsipkan || 0);
    const totalCount = data.total_laporan || 0;
    const menungguKonfirmasi = (data.laporan_per_status as any)?.menunggu_konfirmasi || 0;

    let text = "";

    if (menungguKonfirmasi > 0) {
      text += `${menungguKonfirmasi} laporan menunggu konfirmasi Anda. `;
    }

    if (overdueCount > 0) {
      text += `Terdapat ${overdueCount} laporan yang mengalami keterlambatan. `;
    } else {
      text += `Tidak ada laporan yang terlambat saat ini. `;
    }

    if (totalCount > 0) {
      const completionRate = Math.round((selesaiCount / totalCount) * 100);
      text += `Tingkat penyelesaian: ${completionRate}%.`;
    }

    return text;
  };

  // ✅ NEW: Get trend title based on granularity
  const getTrendTitle = () => {
    switch (trendGranularity) {
      case 'harian':
        return 'Tren Laporan 30 Hari Terakhir';
      case 'mingguan':
        return 'Tren Laporan 12 Minggu Terakhir';
      case 'bulanan':
      default:
        return 'Tren Laporan 6 Bulan Terakhir';
    }
  };

  // ========== COMPUTED DATA ==========
  const totalSelesaiFinal =
    (data.laporan_per_status?.selesai || 0) +
    ((data.laporan_per_status as any)?.diarsipkan || 0);

  const kpiCards = [
    {
      title: 'Total Laporan',
      value: data.total_laporan,
      icon: <FileText className="w-5 h-5" />,
      color: 'text-teal-main',
      bgIcon: 'bg-teal-main/10',
      subtitle: 'Semua draf hingga arsip',
      pulse: false
    },
    {
      title: 'Selesai',
      value: totalSelesaiFinal,
      icon: <CheckCircle className="w-5 h-5" />,
      color: 'text-success',
      bgIcon: 'bg-success/10',
      subtitle: 'Selesai + Diarsipkan',
      pulse: false
    },
    {
      title: 'Antrean RAPIM',
      value: antreanRapim.length,
      icon: <Gavel className="w-5 h-5" />,
      color: 'text-purple-600',
      bgIcon: 'bg-purple-100',
      subtitle: 'Menunggu penjadwalan',
      pulse: antreanRapim.length > 0
    },
    {
      title: 'M. Konfirmasi',
      value: (data.laporan_per_status as any)?.['menunggu_konfirmasi'] || 0,
      icon: <Shield className="w-5 h-5" />,
      color: 'text-amber-700',
      bgIcon: 'bg-amber-100',
      subtitle: 'Menunggu validasi Rektor',
      pulse: ((data.laporan_per_status as any)?.['menunggu_konfirmasi'] || 0) > 0
    },
    {
      title: 'Terlambat',
      value: data.laporan_overdue,
      icon: <AlertTriangle className="w-5 h-5" />,
      color: 'text-danger',
      bgIcon: 'bg-danger/10',
      subtitle: 'Melewati batas waktu',
      pulse: data.laporan_overdue > 0
    },
    {
      title: 'Rata-rata',
      value: `${data.rata_rata_penyelesaian} hari`,
      icon: <Clock className="w-5 h-5" />,
      color: 'text-info',
      bgIcon: 'bg-info/10',
      subtitle: 'Durasi penyelesaian',
      pulse: false
    },
  ];

  const riskPieData = Object.entries(data.laporan_per_risiko).map(([key, value]) => ({
    name: key === 'kritis' ? 'Kritis' : key.charAt(0).toUpperCase() + key.slice(1),
    value,
    fill: RISK_COLORS[key] || '#6B7280',
  }));

  // Status Pie Data
  const statusPieData = Object.entries(data.laporan_per_status)
    .filter(([key]) => key !== 'tugas_ppepp_aktif')
    .filter(([, value]) => value > 0)
    .map(([key, value]) => ({
      name: STATUS_LABELS_SHORT[key] || key,
      value,
      fill: CHART_COLORS[key] || '#6B7280',
    }));

  const kategoriChartData = ((data as any).laporan_per_kategori || []).map((item: any, idx: number) => ({
    name: item.kategori,
    jumlah: item.jumlah,
    fill: item.color || KATEGORI_COLORS[idx % KATEGORI_COLORS.length],
  }));

  const rapimItems = ((data as any).rapim_terdekat?.length
    ? (data as any).rapim_terdekat
    : rapimTerdekatFallback) || [];

  const formatRapimDate = (value?: string | null) => {
    if (!value) return '-';
    const dt = new Date(value);
    return dt.toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const completionRate = data.total_laporan > 0
    ? Math.round((totalSelesaiFinal / data.total_laporan) * 100)
    : 0;

  // ========== LOADING STATE ==========
  if (loading) {
    return <Loading variant="page" message="Memuat data dashboard..." />;
  }

  // ========== RENDER ==========
  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Dashboard' }, { label: 'Rektor' }]} />

      {/* Hero Banner & Smart Insights */}
      <div className="relative overflow-hidden rounded-2xl bg-teal-dark p-6 md:p-8 text-white shadow-lg">
        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 rounded-full bg-white opacity-5 mix-blend-overlay"></div>
        <div className="absolute bottom-0 right-24 -mb-16 w-40 h-40 rounded-full bg-teal-400 opacity-10 blur-2xl"></div>

        <div className="relative z-10">
          <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight mb-2">Dashboard Eksekutif</h1>
          <p className="text-teal-100 text-sm leading-relaxed mb-4">
            Ringkasan performa resolusi masalah dan monitoring progress universitas.
          </p>

          <div className="inline-flex items-start gap-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-3 shadow-sm">
            <div className="p-1.5 bg-amber-400/20 rounded-lg shrink-0">
              <Sparkles className="w-4 h-4 text-amber-300" />
            </div>
            <div>
              <h4 className="text-xs font-semibold text-white mb-0.5">Smart Insight</h4>
              <p className="text-[11px] text-teal-50 leading-relaxed">
                {generateInsights()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Alert RAPIM Banner */}
      {antreanRapim.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-start gap-3 shadow-sm">
          <div className="p-2 bg-purple-100 rounded-lg shrink-0">
            <Gavel className="w-5 h-5 text-purple-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-purple-900">Perhatian: Ada Laporan Menunggu RAPIM</h3>
            <p className="text-sm text-purple-800 mt-1">
              Terdapat <strong>{antreanRapim.length} laporan</strong> yang telah lulus uji Validasi Wakil Rektor dan perlu dijadwalkan dalam Rapat Pimpinan.
            </p>
          </div>
          <Link to="/rapim">
            <button className="whitespace-nowrap px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm">
              Kelola RAPIM
            </button>
          </Link>
        </div>
      )}

      {/* KPI Bento Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        {kpiCards.map((kpi, i) => (
          <div
            key={i}
            className="bg-white rounded-xl border border-neutral-border p-4 shadow-sm hover:shadow-md transition-all relative overflow-hidden group"
          >
            <div className={`absolute top-0 left-0 w-1 h-full bg-gradient-to-b ${kpi.pulse ? 'from-red-400 to-red-600 animate-pulse' : 'from-transparent to-transparent group-hover:from-teal-main/50 group-hover:to-teal-main/10'}`}></div>

            <div className="flex justify-between items-start mb-3">
              <div className={`p-2 rounded-lg ${kpi.bgIcon} ${kpi.color}`}>
                {kpi.icon}
              </div>
              {kpi.pulse && (
                <span className="flex h-2.5 w-2.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-danger opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-danger"></span>
                </span>
              )}
            </div>

            <div>
              <p className="text-2xl font-black text-neutral-text-primary tracking-tight">{kpi.value}</p>
              <h3 className="text-xs font-bold text-neutral-text-secondary mt-0.5">{kpi.title}</h3>
              <p className="text-[9px] text-neutral-border-dark mt-0.5 line-clamp-1">{kpi.subtitle}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ✅ UPDATED: Charts Row - Trend (with Toggle) + Risk */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Trend Chart with Granularity Toggle */}
        <Card className="lg:col-span-2">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-teal-dark">{getTrendTitle()}</h3>
              {trendLoading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-main"></div>
              )}
            </div>

            {/* ✅ NEW: Granularity Toggle Buttons */}
            <div className="flex items-center gap-1 bg-neutral-bg rounded-lg p-1">
              {TREND_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  onClick={() => setTrendGranularity(option.id)}
                  disabled={trendLoading}
                  className={`
                    flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all
                    ${trendGranularity === option.id
                      ? 'bg-white text-teal-main shadow-sm'
                      : 'text-neutral-text-secondary hover:text-teal-main hover:bg-white/50'
                    }
                    ${trendLoading ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  {option.icon}
                  <span className="hidden sm:inline">{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Chart Content */}
          {trendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              {trendGranularity === 'harian' ? (
                // ✅ Bar Chart untuk Harian (lebih readable dengan banyak data)
                <BarChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 9, fill: '#6B7280' }}
                    interval={4} // Show every 5th label for daily
                    angle={-45}
                    textAnchor="end"
                    height={50}
                  />
                  <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #E5E7EB',
                      borderRadius: '12px',
                      fontSize: '11px',
                    }}
                    // ✅ FIX: Hapus type annotation, gunakan nullish coalescing
                    formatter={(value, name) => [
                      value ?? 0,
                      name === 'laporan_masuk' ? 'Masuk' : 'Selesai'
                    ]}
                    labelFormatter={(label) => `Tanggal: ${label}`}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: '11px' }}
                    formatter={(value) => (value === 'laporan_masuk' ? 'Masuk' : 'Selesai')}
                  />
                  <Bar dataKey="laporan_masuk" fill="#0f33ab" name="laporan_masuk" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="laporan_selesai" fill="#10B981" name="laporan_selesai" radius={[2, 2, 0, 0]} />
                </BarChart>
              ) : (
                // ✅ Line Chart untuk Mingguan/Bulanan
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: '#6B7280' }}
                  />
                  <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #E5E7EB',
                      borderRadius: '12px',
                      fontSize: '11px',
                    }}
                    // ✅ FIX: Hapus type annotation, gunakan nullish coalescing
                    formatter={(value, name) => [
                      value ?? 0,
                      name === 'laporan_masuk' ? 'Masuk' : 'Selesai'
                    ]}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: '11px' }}
                    formatter={(value) => (value === 'laporan_masuk' ? 'Masuk' : 'Selesai')}
                  />
                  <Line
                    type="monotone"
                    dataKey="laporan_masuk"
                    stroke="#0f33ab"
                    strokeWidth={2.5}
                    dot={{ fill: '#0f33ab', r: 3 }}
                    name="laporan_masuk"
                  />
                  <Line
                    type="monotone"
                    dataKey="laporan_selesai"
                    stroke="#10B981"
                    strokeWidth={2}
                    dot={{ fill: '#10B981', r: 2.5 }}
                    name="laporan_selesai"
                  />
                </LineChart>
              )}
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-neutral-text-secondary">
              {trendLoading ? 'Memuat data tren...' : 'Belum ada data tren'}
            </div>
          )}

          {/* ✅ NEW: Summary Stats below chart */}
          {trendData.length > 0 && (
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-neutral-border">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-[#0f33ab]"></div>
                  <span className="text-xs text-neutral-text-secondary">
                    Total Masuk:{' '}
                    <span className="font-bold text-neutral-text-primary">
                      {trendData.reduce((sum, d) => sum + d.laporan_masuk, 0)}
                    </span>
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-[#10B981]"></div>
                  <span className="text-xs text-neutral-text-secondary">
                    Total Selesai:{' '}
                    <span className="font-bold text-neutral-text-primary">
                      {trendData.reduce((sum, d) => sum + d.laporan_selesai, 0)}
                    </span>
                  </span>
                </div>
              </div>
              <span className="text-[10px] text-neutral-text-secondary">
                {trendGranularity === 'harian' && '30 hari terakhir'}
                {trendGranularity === 'mingguan' && '12 minggu terakhir'}
                {trendGranularity === 'bulanan' && '6 bulan terakhir'}
              </span>
            </div>
          )}
        </Card>

        {/* Risk Donut */}
        <Card>
          <h3 className="text-base font-bold text-teal-dark mb-3">Distribusi Risiko</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={riskPieData}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={90}
                paddingAngle={4}
                dataKey="value"
              >
                {riskPieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #E5E7EB',
                  borderRadius: '12px',
                  fontSize: '11px',
                }}
              />
              <Legend
                verticalAlign="bottom"
                iconSize={8}
                formatter={(value: string) => (
                  <span className="text-[10px] text-neutral-text-secondary">{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* ========== REST OF THE DASHBOARD ========== */}

      {/* 2-COLUMN LAYOUT: Status/Kategori + Overdue */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* KOLOM KIRI */}
        <div className="space-y-6">

          {/* 1. Alur Status Laporan */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-teal-dark">Alur Status Laporan</h3>
              <Link to="/laporan" className="text-xs text-teal-main hover:underline">
                Lihat Semua
              </Link>
            </div>

            <div className="grid grid-cols-4 gap-1.5">
              {STATUS_FLOW.map((status, index) => {
                const count = status.key === 'selesai'
                  ? totalSelesaiFinal
                  : ((data.laporan_per_status as any)?.[status.key] || 0);
                const IconComponent = status.icon;
                const isActive = count > 0;
                const isLast = index === STATUS_FLOW.length - 1;

                return (
                  <div
                    key={status.key}
                    className={`flex flex-col items-center p-1.5 rounded-lg border transition-all ${isActive
                      ? 'bg-white border-neutral-border/50 shadow-sm'
                      : 'bg-neutral-bg/30 border-transparent'
                      } ${isLast && isActive ? 'ring-1 ring-success/30' : ''}`}
                  >
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center mb-0.5 ${isActive ? status.bgColor : 'bg-neutral-bg'
                        }`}
                      style={{ color: isActive ? status.color : '#9CA3AF' }}
                    >
                      <IconComponent className="w-3 h-3" />
                    </div>
                    <p
                      className="text-sm font-black leading-none"
                      style={{ color: isActive ? status.color : '#D1D5DB' }}
                    >
                      {count}
                    </p>
                    <p className={`text-[7px] font-medium text-center leading-tight mt-0.5 ${isActive ? 'text-neutral-text-secondary' : 'text-neutral-text-secondary/50'
                      }`}>
                      {status.label}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Summary */}
            <div className="flex items-center justify-between mt-3 pt-2 border-t border-neutral-border">
              <div className="flex items-center gap-2">
                <span className="text-[9px] text-neutral-text-secondary">
                  <span className="font-bold text-warning">
                    {(data.laporan_per_status?.berlangsung || 0) + ((data.laporan_per_status as any)?.menunggu_konfirmasi || 0)}
                  </span> proses
                </span>
                <span className="text-[9px] text-neutral-text-secondary">
                  <span className="font-bold text-success">{totalSelesaiFinal}</span> selesai
                </span>
              </div>
              <span className="text-[10px] font-bold text-teal-main">{completionRate}%</span>
            </div>
          </Card>

          {/* 2. Distribusi Status (Donut) */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-teal-dark">Distribusi Status</h3>
              <span className="text-xs text-neutral-text-secondary">
                {statusPieData.reduce((sum, item) => sum + item.value, 0)} aktif
              </span>
            </div>

            {statusPieData.length > 0 ? (
              <div className="flex items-center gap-4">
                <div className="w-1/2">
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie
                        data={statusPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {statusPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [`${value ?? 0}`, '']}
                        contentStyle={{
                          backgroundColor: '#fff',
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px',
                          fontSize: '10px',
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="w-1/2 grid grid-cols-1 gap-1">
                  {statusPieData.slice(0, 6).map((item, i) => (
                    <div key={i} className="flex items-center gap-2 p-1 rounded bg-neutral-bg/50">
                      <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: item.fill }} />
                      <span className="text-[9px] text-neutral-text-secondary flex-1 truncate">{item.name}</span>
                      <span className="text-xs font-bold text-neutral-text-primary">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[160px] flex items-center justify-center text-neutral-text-secondary text-sm">
                Belum ada data
              </div>
            )}
          </Card>
        </div>

        {/* KOLOM KANAN */}
        <div className="space-y-6">

          {/* 1. Laporan per Kategori */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-teal-dark">Laporan per Kategori</h3>
              <BarChart3 className="w-4 h-4 text-neutral-text-secondary" />
            </div>
            {kategoriChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={kategoriChartData}
                    dataKey="jumlah"
                    nameKey="name"
                    cx="32%"
                    cy="50%"
                    outerRadius={70}
                    stroke="#FFFFFF"
                    strokeWidth={2}
                  >
                    {kategoriChartData.map((entry: any, index: number) => (
                      <Cell key={`cat-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: any) => [`${value ?? 0}`, '']}
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '10px',
                    }}
                  />
                  <Legend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    iconType="circle"
                    iconSize={6}
                    formatter={(value: string) => (
                      <span className="text-[9px] text-neutral-text-primary">{value}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[180px] flex items-center justify-center text-neutral-text-secondary text-sm">
                Belum ada data
              </div>
            )}
          </Card>

          {/* 2. Laporan Terlambat */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-red-50 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                </div>
                <h3 className="text-base font-bold text-teal-dark">Terlambat</h3>
              </div>
              <Badge variant="risk" risk="kritis">{data.laporan_overdue}</Badge>
            </div>

            <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
              {(data.overdue_list && data.overdue_list.length > 0) ? (
                data.overdue_list.slice(0, 4).map((item: any, i: number) => (
                  <Link
                    key={i}
                    to={`/laporan/${item.id}`}
                    className="flex items-center gap-2 p-2 rounded-lg bg-red-50/50 border border-red-100 hover:bg-red-50 transition-colors group"
                  >
                    <div className="text-center shrink-0 w-8">
                      <p className="text-base font-black text-danger leading-none">
                        {normalizeOverdueDays(item.hari_terlambat ?? item.hari)}
                      </p>
                      <p className="text-[7px] text-danger font-semibold">hari</p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 mb-0.5">
                        <span className="text-[9px] font-mono text-teal-main">{item.kode}</span>
                        <Badge variant="risk" risk={toRiskLevel(item.risiko || item.tingkat_risiko)} />
                      </div>
                      <p className="text-[10px] font-medium text-neutral-text-primary truncate">{item.judul}</p>
                    </div>
                    <ArrowUpRight className="w-3 h-3 text-neutral-text-secondary opacity-0 group-hover:opacity-100 shrink-0" />
                  </Link>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-6">
                  <div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center mb-1.5">
                    <CheckCircle className="w-4 h-4 text-success" />
                  </div>
                  <p className="text-[10px] font-bold text-neutral-text-primary">Tidak ada yang terlambat!</p>
                </div>
              )}
            </div>

            {data.laporan_overdue > 4 && (
              <Link
                to="/laporan?status=overdue"
                className="mt-2 pt-2 border-t border-neutral-border text-[10px] text-teal-main hover:underline flex items-center gap-0.5 justify-center"
              >
                Lihat semua {data.laporan_overdue} <ChevronRight className="w-3 h-3" />
              </Link>
            )}
          </Card>
        </div>
      </div>

      {/* Antrean RAPIM */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
            <Gavel className="w-5 h-5 text-purple-600" />
            Antrean Rapat Pimpinan (RAPIM)
          </h3>
          <Link
            to="/rapim"
            className="text-xs font-semibold text-teal-main hover:underline flex items-center gap-1"
          >
            Kelola RAPIM <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="space-y-2">
          {antreanRapim.length > 0 ? (
            antreanRapim.map((lap, i) => (
              <div
                key={lap.id || i}
                className="flex items-center gap-3 p-3 rounded-xl border border-purple-200 bg-purple-50/50 hover:bg-purple-50 transition-colors"
              >
                <div className="p-2 rounded-lg bg-purple-100">
                  <Gavel className="w-4 h-4 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-mono text-teal-main">
                      {lap.kode}
                    </span>
                    <Badge
                      variant="risk"
                      risk={(lap.tingkat_risiko || lap.risiko || 'sedang') as TingkatRisiko}
                    />
                  </div>
                  <p className="text-sm font-medium truncate">{lap.judul}</p>
                  <p className="text-xs text-neutral-text-secondary">
                    {lap.unit?.name || '-'} • Diajukan: {formatDate(lap.created_at)}
                  </p>
                </div>
                <Link to={`/laporan/${lap.id}`}>
                  <button className="p-2 rounded-lg hover:bg-purple-100 text-purple-600 transition-colors">
                    <ArrowUpRight className="w-4 h-4" />
                  </button>
                </Link>
              </div>
            ))
          ) : (
            <div className="text-center py-6">
              <Gavel className="w-8 h-8 mx-auto mb-2 text-neutral-text-secondary opacity-40" />
              <p className="text-sm text-neutral-text-secondary">
                Tidak ada laporan dalam antrean RAPIM
              </p>
              <p className="text-xs text-neutral-text-secondary mt-1">
                Laporan akan muncul setelah lolos validasi Level 2
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Upcoming RAPIM */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-teal-dark">Agenda RAPIM Terdekat</h3>
          <Link to="/rapim" className="text-xs text-teal-main hover:underline">Lihat Semua</Link>
        </div>
        {rapimItems.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {rapimItems.slice(0, 3).map((item: any) => (
              <Link
                key={item.id}
                to={`/rapim/${item.id}`}
                className="flex items-start gap-3 p-3 rounded-xl border border-neutral-border hover:border-teal-main/30 transition-all"
              >
                <div className="w-9 h-9 rounded-lg bg-info/10 text-info flex items-center justify-center shrink-0">
                  <Calendar className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-neutral-text-primary truncate">{item.judul}</p>
                  <p className="text-[10px] text-neutral-text-secondary mt-0.5">{formatRapimDate(item.tanggal)}</p>
                  <div className="flex gap-1.5 mt-1">
                    <span className="text-[9px] px-1.5 py-0.5 bg-neutral-bg rounded">{item.jumlah_laporan ?? 0} laporan</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded ${item.status === 'berlangsung' ? 'bg-success/10 text-success' : 'bg-info/10 text-info'
                      }`}>
                      {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 bg-neutral-bg/30 rounded-xl border border-dashed border-neutral-border">
            <p className="text-xs text-neutral-text-secondary">Belum ada agenda RAPIM mendatang</p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default DashboardRektor;
